import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { vaccineModel, vaccinePetsModel } from 'entities/vaccine';

import {
  CreatePharmaceuticalCompanyDto,
  GetPharmaceuticalCompaniesParams,
  PaginatedResponse,
  Pagination,
  PharmaceuticalCompanyDetail,
  PharmaceuticalCompanyLightItem,
  PharmaceuticalCompanyListItem,
  UpdatePharmaceuticalCompanyDto,
  UpdatePharmaceuticalCompanyResponse,
  typicodeApi,
} from 'shared/api';

type TPharmaCompanyState = {
  list: PharmaceuticalCompanyListItem[];
  lightList: PharmaceuticalCompanyLightItem[];
  pagination: Pagination | null;
};

const initialState: TPharmaCompanyState = {
  list: [],
  lightList: [],
  pagination: null,
};

export const pharmaceuticalCompanyModel = createSlice({
  name: 'pharmaCompanies',
  initialState,
  reducers: {
    setList: (state, { payload }: PayloadAction<PharmaceuticalCompanyListItem[]>) => {
      state.list = payload;
    },
    setLightList: (state, { payload }: PayloadAction<PharmaceuticalCompanyLightItem[]>) => {
      state.lightList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
  },
});

export const PHARMA_COMPANIES_ALL_QUERY_KEY = 'pharma-companies-all';
const PHARMA_COMPANIES_LIGHT_QUERY_KEY = 'pharma-companies-light';
const PHARMA_COMPANY_BY_ID_QUERY_KEY = 'pharma-company-by-id';

export const getPharmaCompaniesAllAsync = (params: GetPharmaceuticalCompaniesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<PharmaceuticalCompanyListItem>>>(
    [PHARMA_COMPANIES_ALL_QUERY_KEY, params],
    () => typicodeApi.pharmaceuticalCompany.getPharmaceuticalCompaniesAll(params),
    {
      onSuccess: ({ data }) => {
        dispatch(pharmaceuticalCompanyModel.actions.setList(data.data));
        dispatch(pharmaceuticalCompanyModel.actions.setPagination(data.pagination));
      },
      onError: () => {
        toast.error('There was an error loading pharmaceutical companies, please try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

export const useLightPharmaCompanies = (enabled: boolean = true) =>
  useQuery<AxiosResponse<PharmaceuticalCompanyLightItem[]>>(
    [PHARMA_COMPANIES_LIGHT_QUERY_KEY],
    () => typicodeApi.pharmaceuticalCompany.getPharmaceuticalCompaniesLight(),
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

export const getPharmaCompanyByIdAsync = (id: string, enabled: boolean = true) =>
  useQuery<AxiosResponse<PharmaceuticalCompanyDetail>>(
    [PHARMA_COMPANY_BY_ID_QUERY_KEY, id],
    () => typicodeApi.pharmaceuticalCompany.getPharmaceuticalCompanyById(id),
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );

export const createPharmaCompanyAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreatePharmaceuticalCompanyDto) => typicodeApi.pharmaceuticalCompany.postPharmaceuticalCompany(data),
    {
      onSuccess: () => {
        toast.success('Pharmaceutical company created successfully!');
        void queryClient.invalidateQueries(PHARMA_COMPANIES_ALL_QUERY_KEY);
        void queryClient.invalidateQueries(PHARMA_COMPANIES_LIGHT_QUERY_KEY);
      },
      onError: () => {
        toast.error('Error creating pharmaceutical company. Try again!');
      },
    },
  );
};

export const updatePharmaCompanyAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: UpdatePharmaceuticalCompanyDto }) =>
      typicodeApi.pharmaceuticalCompany.patchPharmaceuticalCompany(id, data),
    {
      onSuccess: ({ data }: AxiosResponse<UpdatePharmaceuticalCompanyResponse>) => {
        const cascaded = data.cascadedVaccines;
        if (cascaded > 0) {
          toast.success(`Pharmaceutical company updated. Cascaded ${cascaded} vaccine(s).`);
          void queryClient.invalidateQueries(vaccineModel.VACCINES_ALL_QUERY_KEY);
          void queryClient.invalidateQueries(vaccinePetsModel.VACCINES_PETS_ALL_QUERY_KEY);
        } else {
          toast.success('Pharmaceutical company updated successfully!');
        }
        void queryClient.invalidateQueries(PHARMA_COMPANIES_ALL_QUERY_KEY);
        void queryClient.invalidateQueries(PHARMA_COMPANIES_LIGHT_QUERY_KEY);
      },
      onError: () => {
        toast.error('Error updating pharmaceutical company. Try again!');
      },
    },
  );
};

export const deletePharmaCompanyAsync = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => typicodeApi.pharmaceuticalCompany.deletePharmaceuticalCompanyById(id), {
    onSuccess: () => {
      toast.success('Pharmaceutical company deleted successfully!');
      void queryClient.invalidateQueries(PHARMA_COMPANIES_ALL_QUERY_KEY);
      void queryClient.invalidateQueries(PHARMA_COMPANIES_LIGHT_QUERY_KEY);
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        toast.error('Cannot delete: vaccines are linked to this company. Re-link them first.');
      } else {
        toast.error('Error deleting pharmaceutical company. Try again!');
      }
    },
  });
};

export const usePharmaCompanyList = () =>
  useSelector((state: RootState) => ({
    list: state.pharmaCompanies.list,
    pagination: state.pharmaCompanies.pagination,
  }));

export const useLightPharmaCompanyList = () => useSelector((state: RootState) => state.pharmaCompanies.lightList);

export const reducer = pharmaceuticalCompanyModel.reducer;
